const DB_NAME = "transcription_drafts";
const STORE_NAME = "recording_drafts";
const DB_VERSION = 1;

function openDB() {
  return new Promise((resolve, reject) => {
    if (typeof indexedDB === "undefined") {
      reject(new Error("IndexedDB is not available"));
      return;
    }
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onerror = () => reject(request.error || new Error("Failed to open IndexedDB"));
    request.onsuccess = () => resolve(request.result);
    request.onupgradeneeded = (event) => {
      const db = event.target.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: "visitId" });
      }
    };
  });
}

/**
 * @param {string|number} visitId
 * @param {Blob} blob
 * @param {{ durationSec?: number, waveformPeaks?: number[] }} [meta]
 */
export async function saveDraftToIndexedDB(visitId, blob, meta = {}) {
  if (!visitId || !blob || blob.size === 0) return;
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, "readwrite");
    const store = tx.objectStore(STORE_NAME);
    store.put({
      visitId: String(visitId),
      blob,
      mimeType: blob.type || "audio/webm",
      durationSec: Number(meta.durationSec) || 0,
      waveformPeaks: Array.isArray(meta.waveformPeaks) ? meta.waveformPeaks : null,
      updatedAt: Date.now(),
    });
    tx.oncomplete = () => {
      db.close();
      resolve();
    };
    tx.onerror = () => {
      db.close();
      reject(tx.error || new Error("Failed to save draft"));
    };
  });
}

/**
 * @param {string|number} visitId
 * @returns {Promise<{ blob: Blob, durationSec: number, waveformPeaks: number[]|null }|null>}
 */
export async function loadDraftFromIndexedDB(visitId) {
  if (!visitId) return null;
  try {
    const db = await openDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, "readonly");
      const store = tx.objectStore(STORE_NAME);
      const req = store.get(String(visitId));
      req.onsuccess = () => {
        db.close();
        const row = req.result;
        if (!row?.blob || !(row.blob instanceof Blob)) {
          resolve(null);
          return;
        }
        resolve({
          blob: row.blob,
          durationSec: Number(row.durationSec) || 0,
          waveformPeaks: Array.isArray(row.waveformPeaks) ? row.waveformPeaks : null,
        });
      };
      req.onerror = () => {
        db.close();
        reject(req.error || new Error("Failed to load draft"));
      };
    });
  } catch {
    return null;
  }
}

/**
 * @param {string|number} visitId
 */
export async function deleteDraftFromIndexedDB(visitId) {
  if (!visitId) return;
  try {
    const db = await openDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, "readwrite");
      const store = tx.objectStore(STORE_NAME);
      store.delete(String(visitId));
      tx.oncomplete = () => {
        db.close();
        resolve();
      };
      tx.onerror = () => {
        db.close();
        reject(tx.error || new Error("Failed to delete draft"));
      };
    });
  } catch {
    /* ignore */
  }
}
